import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftRight,
  ArrowRight,
  Briefcase,
  GripVertical,
  History,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Users,
} from 'lucide-react';
import { supabase_get_bullhorn_sync_logs } from '../services/supabaseService';
import { BullhornAuthData, fetchBullhornCandidatesRaw, fetchBullhornJobOrdersRaw } from '../services/bullhornService';
import {
  BULLHORN_FIELDS,
  BullhornMappingEntity,
  cycleDirection,
  DIRECTION_LABELS,
  ENTITY_LABELS,
  FieldMappingRule,
  MappingChangeLogEntry,
  mergeMappings,
  saveBullhornFieldMappings,
  SyncDirection,
  WERFTOOLS_FIELDS,
} from '../services/bullhornFieldMappings';
import { supabase } from '../services/supabaseService';

interface Props {
  clientId: string;
  bhAuth?: BullhornAuthData | null;
}

type DragPayload = { side: 'werftools' | 'bullhorn'; field: string };

const BullhornFieldMapper: React.FC<Props> = ({ clientId, bhAuth }) => {
  const [entity, setEntity] = useState<BullhornMappingEntity>('vacancy');
  const [mappings, setMappings] = useState(() => mergeMappings(null));
  const [bullhornSettings, setBullhornSettings] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  const [dragPayload, setDragPayload] = useState<DragPayload | null>(null);
  const [dropHighlight, setDropHighlight] = useState<string | null>(null);
  const [syncLogs, setSyncLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [sampleLocal, setSampleLocal] = useState<any>(null);
  const [sampleBullhorn, setSampleBullhorn] = useState<any>(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleError, setSampleError] = useState<string | null>(null);
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await supabase
        .from('clients')
        .select('bullhorn_settings')
        .eq('id', clientId)
        .single();
      const settings = (data?.bullhorn_settings as Record<string, unknown>) || null;
      setBullhornSettings(settings);
      setMappings(mergeMappings(settings?.fieldMappings as any));
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  const loadSyncLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const rows = await supabase_get_bullhorn_sync_logs(clientId, 50);
      setSyncLogs(rows);
    } finally {
      setLogsLoading(false);
    }
  }, [clientId]);

  const entityMappings = mappings[entity];

  const mappedWt = useMemo(() => new Set(entityMappings.map((m) => m.werftoolsField)), [entityMappings]);
  const mappedBh = useMemo(() => new Set(entityMappings.map((m) => m.bullhornField)), [entityMappings]);

  const unmappedWt = WERFTOOLS_FIELDS[entity].filter((f) => !mappedWt.has(f.key));
  const unmappedBh = BULLHORN_FIELDS[entity].filter((f) => !mappedBh.has(f.key));

  useEffect(() => {
    void loadAll();
    void loadSyncLogs();
  }, [loadAll, loadSyncLogs]);

  useEffect(() => {
    if (entityMappings.length === 0) {
      setSelectedMappingId(null);
      return;
    }
    if (!selectedMappingId || !entityMappings.some((m) => m.id === selectedMappingId)) {
      setSelectedMappingId(entityMappings[0].id);
    }
  }, [entityMappings, selectedMappingId]);

  const loadSampleData = useCallback(async () => {
    setSampleLoading(true);
    setSampleError(null);
    try {
      if (entity === 'vacancy') {
        const { data: local } = await supabase
          .from('vacancies')
          .select('id,title,status,bullhorn_id,data,updated_at')
          .eq('client_id', clientId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        setSampleLocal(local || null);

        if (bhAuth) {
          const bh = await fetchBullhornJobOrdersRaw(bhAuth, 'isDeleted:0', true, false);
          setSampleBullhorn(bh.data?.[0] || null);
        } else {
          setSampleBullhorn(null);
        }
      } else {
        const { data: local } = await supabase
          .from('candidates')
          .select('id,name,phone,bullhorn_id,address_city,address_zip,address_country,source,updated_at')
          .eq('client_id', clientId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        setSampleLocal(local || null);

        if (bhAuth) {
          const bh = await fetchBullhornCandidatesRaw(bhAuth, 'isDeleted:0', false);
          setSampleBullhorn(bh.data?.[0] || null);
        } else {
          setSampleBullhorn(null);
        }
      }
    } catch (err: any) {
      setSampleError(err?.message || String(err));
    } finally {
      setSampleLoading(false);
    }
  }, [bhAuth, clientId, entity]);

  useEffect(() => {
    void loadSampleData();
  }, [loadSampleData]);

  const persist = async (next: typeof mappings, logEntry?: MappingChangeLogEntry) => {
    setIsSaving(true);
    setSaveOk(false);
    try {
      const withLog = logEntry
        ? { ...next, mappingChangeLog: [logEntry, ...(next.mappingChangeLog || [])].slice(0, 200) }
        : next;
      await saveBullhornFieldMappings(clientId, withLog, bullhornSettings);
      setMappings(withLog);
      const { data } = await supabase
        .from('clients')
        .select('bullhorn_settings')
        .eq('id', clientId)
        .single();
      setBullhornSettings((data?.bullhorn_settings as Record<string, unknown>) || null);
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2500);
    } catch (e: any) {
      alert('Opslaan mislukt: ' + (e?.message || e));
    } finally {
      setIsSaving(false);
    }
  };

  const addMapping = (wt: string, bh: string) => {
    if (!wt || !bh) return;
    if (entityMappings.some((m) => m.werftoolsField === wt && m.bullhornField === bh)) return;
    const rule: FieldMappingRule = {
      id: crypto.randomUUID(),
      werftoolsField: wt,
      bullhornField: bh,
      direction: 'both',
    };
    const next = {
      ...mappings,
      [entity]: [...entityMappings, rule],
    };
    setMappings(next);
    void persist(next, {
      at: new Date().toISOString(),
      entity,
      message: `Gekoppeld: ${ENTITY_LABELS[entity].werftools}.${wt} ↔ ${ENTITY_LABELS[entity].bullhorn}.${bh}`,
      werftoolsField: wt,
      bullhornField: bh,
    });
  };

  const removeMapping = (id: string) => {
    const removed = entityMappings.find((m) => m.id === id);
    const next = {
      ...mappings,
      [entity]: entityMappings.filter((m) => m.id !== id),
    };
    setMappings(next);
    void persist(
      next,
      removed
        ? {
            at: new Date().toISOString(),
            entity,
            message: `Verwijderd: ${ENTITY_LABELS[entity].werftools}.${removed.werftoolsField} ↔ ${ENTITY_LABELS[entity].bullhorn}.${removed.bullhornField}`,
            werftoolsField: removed.werftoolsField,
            bullhornField: removed.bullhornField,
          }
        : undefined
    );
  };

  const setDirection = (id: string, direction: SyncDirection) => {
    const next = {
      ...mappings,
      [entity]: entityMappings.map((m) => (m.id === id ? { ...m, direction } : m)),
    };
    setMappings(next);
  };

  const handleDropOnBullhorn = (bhField: string) => {
    if (!dragPayload || dragPayload.side !== 'werftools') return;
    addMapping(dragPayload.field, bhField);
    setDragPayload(null);
    setDropHighlight(null);
  };

  const handleDropOnWerftools = (wtField: string) => {
    if (!dragPayload || dragPayload.side !== 'bullhorn') return;
    addMapping(wtField, dragPayload.field);
    setDragPayload(null);
    setDropHighlight(null);
  };

  const resetDefaults = () => {
    if (!window.confirm('Alle koppelingen voor dit object terugzetten naar de standaard?')) return;
    const defaults = mergeMappings(null);
    const next = { ...mappings, [entity]: defaults[entity] };
    setMappings(next);
    void persist(next, {
      at: new Date().toISOString(),
      entity,
      message: `Standaard mapping hersteld voor ${entity}`,
    });
  };

  const mappingLog = (mappings.mappingChangeLog || []).filter((l) => l.entity === entity || !l.entity);
  const selectedMapping = entityMappings.find((m) => m.id === selectedMappingId) || null;

  const getByPath = (obj: any, path: string): any => {
    if (!obj || !path) return undefined;
    return String(path)
      .split('.')
      .reduce((acc: any, part) => (acc == null ? undefined : acc[part]), obj);
  };

  const formatValue = (v: any): string => {
    if (v == null || v === '') return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v)) return v.join(', ');
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={20} />
        Mapper laden…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-1">
            Dagelijkse 2-weg sync
          </p>
          <h3 className="text-lg font-black text-slate-900">Veldmapper</h3>
          <p className="text-xs text-slate-500 font-medium mt-1 max-w-xl">
            Sleep een Werftools-veld naar een Bullhorn-veld (of omgekeerd) om te koppelen. Richting per veld:
            import, export of beide kanten.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={resetDefaults}
            className="px-4 py-2 rounded-xl border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50"
          >
            Standaard
          </button>
          <button
            type="button"
            onClick={() => void loadSyncLogs()}
            disabled={logsLoading}
            className="px-4 py-2 rounded-xl border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 inline-flex items-center gap-2"
          >
            {logsLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Sync-log
          </button>
          <button
            type="button"
            onClick={() => void persist(mappings)}
            disabled={isSaving}
            className="px-5 py-2 rounded-xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-black inline-flex items-center gap-2 disabled:opacity-50"
          >
            {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saveOk ? 'Opgeslagen' : 'Opslaan'}
          </button>
        </div>
      </div>

      <div className="flex gap-2 p-1 bg-slate-100 rounded-xl w-fit">
        <button
          type="button"
          onClick={() => setEntity('vacancy')}
          className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
            entity === 'vacancy' ? 'bg-white text-pink-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Briefcase size={14} /> Vacatures
        </button>
        <button
          type="button"
          onClick={() => setEntity('candidate')}
          className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
            entity === 'candidate' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Users size={14} /> Kandidaten
        </button>
      </div>

      {/* Active mappings — visual center */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
            Gekoppelde velden ({entityMappings.length})
          </span>
          <span className="text-[10px] font-mono text-slate-400">
            {ENTITY_LABELS[entity].werftools} ↔ {ENTITY_LABELS[entity].bullhorn}
          </span>
        </div>
        <div className="p-4 space-y-2 max-h-[280px] overflow-y-auto custom-scrollbar">
          {entityMappings.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-8">
              Nog geen koppelingen. Sleep velden vanuit de kolommen hieronder.
            </p>
          ) : (
            entityMappings.map((m) => (
              <div
                key={m.id}
                className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl border group cursor-pointer transition-all ${
                  selectedMappingId === m.id
                    ? 'bg-indigo-50/40 border-indigo-200'
                    : 'bg-gradient-to-r from-slate-50 to-white border-slate-100'
                }`}
                onClick={() => setSelectedMappingId(m.id)}
              >
                <div className="flex-1 min-w-0">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    Werftools
                  </span>
                  <p className="font-mono text-xs font-bold text-slate-800 truncate">
                    {ENTITY_LABELS[entity].werftools}.{m.werftoolsField}
                  </p>
                </div>
                <div className="flex items-center justify-center gap-1 shrink-0 px-2">
                  <button
                    type="button"
                    title="Sync-richting wijzigen"
                    onClick={() => {
                      const next = cycleDirection(m.direction);
                      setDirection(m.id, next);
                      void persist(
                        {
                          ...mappings,
                          [entity]: entityMappings.map((r) =>
                            r.id === m.id ? { ...r, direction: next } : r
                          ),
                        },
                        {
                          at: new Date().toISOString(),
                          entity,
                          message: `Richting ${m.werftoolsField}: ${DIRECTION_LABELS[next]}`,
                          werftoolsField: m.werftoolsField,
                          bullhornField: m.bullhornField,
                        }
                      );
                    }}
                    className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider border ${
                      m.direction === 'both'
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                        : m.direction === 'inbound'
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-amber-50 border-amber-200 text-amber-700'
                    }`}
                  >
                    <ArrowLeftRight size={12} className="inline mr-1" />
                    {DIRECTION_LABELS[m.direction]}
                  </button>
                  <ArrowRight size={16} className="text-slate-300 hidden sm:block" />
                </div>
                <div className="flex-1 min-w-0 sm:text-right">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    Bullhorn
                  </span>
                  <p className="font-mono text-xs font-bold text-[#e51b23] truncate">
                    {ENTITY_LABELS[entity].bullhorn}.{m.bullhornField}
                  </p>
                  {m.note && <p className="text-[10px] text-slate-400 italic">{m.note}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => removeMapping(m.id)}
                  className="p-2 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  aria-label="Koppeling verwijderen"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-indigo-50/70 border-b border-indigo-100 flex items-center justify-between">
          <span className="text-[10px] font-black text-indigo-700 uppercase tracking-widest">
            Live voorbeeld (echte data)
          </span>
          <button
            type="button"
            onClick={() => void loadSampleData()}
            disabled={sampleLoading}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-indigo-200 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50"
          >
            {sampleLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Vernieuwen
          </button>
        </div>
        <div className="p-4">
          {!selectedMapping ? (
            <p className="text-xs text-slate-400">Selecteer een mappingregel hierboven om waarden te zien.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3">
                <p className="text-[10px] font-black text-emerald-800 uppercase tracking-widest mb-1">
                  Werftools waarde
                </p>
                <p className="text-[11px] font-mono text-slate-700 break-words">
                  {ENTITY_LABELS[entity].werftools}.{selectedMapping.werftoolsField}
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-900 break-words">
                  {formatValue(getByPath(sampleLocal, selectedMapping.werftoolsField))}
                </p>
              </div>
              <div className="rounded-xl border border-red-100 bg-red-50/40 p-3">
                <p className="text-[10px] font-black text-[#cc171e] uppercase tracking-widest mb-1">
                  Bullhorn waarde
                </p>
                <p className="text-[11px] font-mono text-slate-700 break-words">
                  {ENTITY_LABELS[entity].bullhorn}.{selectedMapping.bullhornField}
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-900 break-words">
                  {bhAuth
                    ? formatValue(getByPath(sampleBullhorn, selectedMapping.bullhornField))
                    : 'Verbind eerst met Bullhorn'}
                </p>
              </div>
            </div>
          )}
          {sampleError && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              Voorbeelddata laden mislukt: {sampleError}
            </p>
          )}
          <p className="mt-3 text-[10px] text-slate-400">
            Voorbeeld gebruikt laatste lokale record + nieuwste Bullhorn record voor dit object.
          </p>
        </div>
      </div>

      {/* Drag columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm">
          <div className="px-4 py-3 bg-emerald-50/80 border-b border-emerald-100">
            <p className="text-[10px] font-black text-emerald-800 uppercase tracking-widest">
              Werftools · {ENTITY_LABELS[entity].werftools}
            </p>
            <p className="text-[10px] text-emerald-700/70 mt-0.5">Sleep naar Bullhorn →</p>
          </div>
          <div className="p-3 flex flex-wrap gap-2 min-h-[120px]">
            {unmappedWt.map((f) => (
              <div
                key={f.key}
                draggable
                onDragStart={() => setDragPayload({ side: 'werftools', field: f.key })}
                onDragEnd={() => {
                  setDragPayload(null);
                  setDropHighlight(null);
                }}
                onDragOver={(e) => {
                  if (dragPayload?.side === 'bullhorn') e.preventDefault();
                }}
                onDrop={() => handleDropOnWerftools(f.key)}
                className={`cursor-grab active:cursor-grabbing inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold transition-all ${
                  dropHighlight === `wt-${f.key}`
                    ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                    : 'border-emerald-200 bg-white text-slate-700 hover:border-emerald-400'
                }`}
              >
                <GripVertical size={12} className="text-emerald-400 shrink-0" />
                <span className="font-mono">{f.key}</span>
              </div>
            ))}
            {unmappedWt.length === 0 && (
              <p className="text-xs text-slate-400 w-full text-center py-4">Alle velden gekoppeld</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-red-100 shadow-sm">
          <div className="px-4 py-3 bg-red-50/80 border-b border-red-100">
            <p className="text-[10px] font-black text-[#cc171e] uppercase tracking-widest">
              Bullhorn · {ENTITY_LABELS[entity].bullhorn}
            </p>
            <p className="text-[10px] text-red-700/70 mt-0.5">← Drop Werftools-veld hier</p>
          </div>
          <div className="p-3 flex flex-wrap gap-2 min-h-[120px]">
            {unmappedBh.map((f) => (
              <div
                key={f.key}
                draggable
                onDragStart={() => setDragPayload({ side: 'bullhorn', field: f.key })}
                onDragEnd={() => {
                  setDragPayload(null);
                  setDropHighlight(null);
                }}
                onDragOver={(e) => {
                  if (dragPayload?.side === 'werftools') {
                    e.preventDefault();
                    setDropHighlight(`bh-${f.key}`);
                  }
                }}
                onDragLeave={() => setDropHighlight(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDropOnBullhorn(f.key);
                }}
                className={`cursor-grab active:cursor-grabbing inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold transition-all ${
                  dropHighlight === `bh-${f.key}`
                    ? 'border-[#e51b23] bg-red-50 ring-2 ring-red-200'
                    : 'border-red-200 bg-white text-slate-700 hover:border-[#e51b23]'
                }`}
              >
                <GripVertical size={12} className="text-red-300 shrink-0" />
                <span className="font-mono">{f.key}</span>
              </div>
            ))}
            {unmappedBh.length === 0 && (
              <p className="text-xs text-slate-400 w-full text-center py-4">Alle velden gekoppeld</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick link: click WT then BH */}
      <QuickLinkRow entity={entity} unmappedWt={unmappedWt} unmappedBh={unmappedBh} onLink={addMapping} />

      {/* Logs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-slate-900 rounded-2xl overflow-hidden border border-slate-800">
          <div className="px-4 py-2 border-b border-slate-700 flex items-center gap-2">
            <History size={14} className="text-indigo-400" />
            <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">
              Mapping-wijzigingen
            </span>
          </div>
          <div className="p-3 max-h-48 overflow-y-auto custom-scrollbar space-y-1.5 font-mono text-[10px]">
            {mappingLog.length === 0 ? (
              <p className="text-slate-500 italic">Nog geen wijzigingen gelogd.</p>
            ) : (
              mappingLog.slice(0, 40).map((l, i) => (
                <div key={`${l.at}-${i}`} className="text-slate-400">
                  <span className="text-slate-500">{new Date(l.at).toLocaleString('nl-NL')}</span>
                  <span className="text-emerald-400/90 ml-2">{l.message}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl overflow-hidden border border-slate-800">
          <div className="px-4 py-2 border-b border-slate-700 flex items-center gap-2">
            <RefreshCw size={14} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">
              Sync-activiteit (database)
            </span>
          </div>
          <div className="p-3 max-h-48 overflow-y-auto custom-scrollbar space-y-1.5 font-mono text-[10px]">
            {syncLogs.length === 0 ? (
              <p className="text-slate-500 italic">Geen sync-logregels (import/export) gevonden.</p>
            ) : (
              syncLogs.map((row) => (
                <div key={row.id} className="text-slate-400">
                  <span className="text-slate-500">{new Date(row.created_at).toLocaleString('nl-NL')}</span>
                  <span
                    className={
                      row.status === 'success' ? 'text-emerald-400 ml-2' : 'text-red-400 ml-2'
                    }
                  >
                    {row.action} · {row.status}
                    {row.bullhorn_candidate_id ? ` · BH#${row.bullhorn_candidate_id}` : ''}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function QuickLinkRow({
  entity,
  unmappedWt,
  unmappedBh,
  onLink,
}: {
  entity: BullhornMappingEntity;
  unmappedWt: { key: string }[];
  unmappedBh: { key: string }[];
  onLink: (wt: string, bh: string) => void;
}) {
  const [wt, setWt] = useState('');
  const [bh, setBh] = useState('');

  return (
    <div className="flex flex-col sm:flex-row gap-2 items-end p-4 bg-slate-50 rounded-2xl border border-slate-100">
      <div className="flex-1 space-y-1 w-full">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
          Werftools-veld
        </label>
        <select
          value={wt}
          onChange={(e) => setWt(e.target.value)}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-mono bg-white"
        >
          <option value="">Kies…</option>
          {unmappedWt.map((f) => (
            <option key={f.key} value={f.key}>
              {ENTITY_LABELS[entity].werftools}.{f.key}
            </option>
          ))}
        </select>
      </div>
      <Plus size={20} className="text-slate-300 shrink-0 hidden sm:block mb-2" />
      <div className="flex-1 space-y-1 w-full">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
          Bullhorn-veld
        </label>
        <select
          value={bh}
          onChange={(e) => setBh(e.target.value)}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-mono bg-white"
        >
          <option value="">Kies…</option>
          {unmappedBh.map((f) => (
            <option key={f.key} value={f.key}>
              {ENTITY_LABELS[entity].bullhorn}.{f.key}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        disabled={!wt || !bh}
        onClick={() => {
          onLink(wt, bh);
          setWt('');
          setBh('');
        }}
        className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-40 hover:bg-indigo-700"
      >
        Koppelen
      </button>
    </div>
  );
}

export default BullhornFieldMapper;
